import React, { useEffect, useState } from "react";
import { Collapse, Col, Container, Row } from "reactstrap";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchFaq } from "@/shared/apiHelper";
import { ParticipantTabNavs } from "@/shared/components/tabNavs";
import Sidebar from "@/containers/Layout/sidebar";
import ClearPageLayout from "@/containers/Participant/components/ClearPageLayout";
import { PARTICIPANT_FAQS } from "./components/Queries";

const SUPPORT_URL = "https://incentco.zendesk.com/hc/en-us/requests/new";
const CLASSIC_IMG_BACK = `${process.env.PUBLIC_URL}/theme/classic/img/pages/my-points.jpg`;

const stripHtml = (value = "") =>
  value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();

// Plain text of an answer, which may be a string or JSX, so search can match both
const nodeText = (node) => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(" ");
  return nodeText(node.props?.children);
};

const slugify = (value = "") =>
  stripHtml(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildFaqDocument = (html = "") => {
  const headings = [];
  const usedIds = new Set();

  const processedContent = html.replace(
    /<h([1-6])([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (match, level, attrs = "", inner = "") => {
      const text = stripHtml(inner);

      if (!text) return match;

      const existingIdMatch = attrs.match(/\sid=(['"])(.*?)\1/i);
      let id =
        existingIdMatch?.[2] ||
        slugify(text) ||
        `faq-section-${headings.length + 1}`;

      const baseId = id;
      let suffix = 2;

      while (usedIds.has(id)) {
        id = `${baseId}-${suffix}`;
        suffix += 1;
      }

      usedIds.add(id);
      headings.push({ id, text });

      if (existingIdMatch && existingIdMatch[2] === id) {
        return match;
      }

      const nextAttrs = existingIdMatch
        ? attrs.replace(/\sid=(['"])(.*?)\1/i, ` id="${id}"`)
        : `${attrs} id="${id}"`;

      return `<h${level}${nextAttrs}>${inner}</h${level}>`;
    }
  );

  return { headings, processedContent };
};

const FaqLoadingState = () => (
  <div className="faq-panel__loading">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="faq-panel__loading-line skeleton" />
    ))}
  </div>
);

const FaqEmptyState = ({ onClear }) => (
  <div className="faq-panel__empty">
    <p>No questions matched that search.</p>
    <button type="button" className="faq-panel__clear" onClick={onClear}>
      Clear search
    </button>
  </div>
);

const FaqContentSection = ({
  faqDocument,
  filteredQuestions,
  loading,
  activeQuestion,
  setActiveQuestion,
  query,
  setQuery,
  t,
}) => {
  const openQuestion = filteredQuestions.some(
    (item) => item.question === activeQuestion
  )
    ? activeQuestion
    : filteredQuestions[0]?.question;

  const panelHeader = (
    <div className="faq-panel__header">
      <div>
        <h2 className="faq-panel__title">{t("faqs")}</h2>
        <p className="faq-panel__desc">{t("congratulations_faq")}</p>
      </div>
      <a
        href={SUPPORT_URL}
        target="_blank"
        rel="noreferrer"
        className="faq-panel__support"
      >
        {t("support_request")}
      </a>
    </div>
  );

  if (loading) {
    return (
      <section className="faq-panel">
        {panelHeader}
        <FaqLoadingState />
      </section>
    );
  }

  if (faqDocument?.processedContent) {
    return (
      <section className="faq-panel">
        {panelHeader}
        {faqDocument.headings.length > 0 && (
          <div className="faq-panel__anchors">
            {faqDocument.headings.slice(0, 6).map((item) => (
              <a key={item.id} href={`#${item.id}`} className="faq-panel__anchor">
                {item.text}
              </a>
            ))}
          </div>
        )}
        <div className="faq-panel__article">
          <div
            className="faq-panel__prose"
            dangerouslySetInnerHTML={{ __html: faqDocument.processedContent }}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="faq-panel">
      {panelHeader}

      <div className="faq-panel__search-wrap">
        <label className="faq-panel__search">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search common questions"
            aria-label="Search common questions"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")}>
              ×
            </button>
          )}
        </label>
      </div>

      {filteredQuestions.length > 0 ? (
        <div className="faq-panel__accordion">
          {filteredQuestions.map((item) => {
            const isOpen = openQuestion === item.question;

            return (
              <div
                key={item.question}
                className={`faq-panel__item${isOpen ? " is-open" : ""}`}
              >
                <button
                  type="button"
                  className="faq-panel__question"
                  onClick={() =>
                    setActiveQuestion(isOpen ? null : item.question)
                  }
                >
                  <span className="faq-panel__question-text">{item.question}</span>
                  <span className="faq-panel__question-icon">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>

                <Collapse isOpen={isOpen}>
                  <div className="faq-panel__answer">
                    {typeof item.answer === "string" ? (
                      <p>{item.answer.trim()}</p>
                    ) : (
                      item.answer
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
      ) : (
        <FaqEmptyState onClear={() => setQuery("")} />
      )}
    </section>
  );
};

const Faqs = ({ template, program }) => {
  const { t } = useTranslation();
  const [faq, setFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeQuestion, setActiveQuestion] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (!program?.organization_id || !program?.id) return undefined;

    const loadFaq = async () => {
      setLoading(true);

      try {
        const data = await fetchFaq({
          organizationId: program.organization_id,
          programId: program.id,
          audienceType: "participant",
        });

        if (isMounted) {
          setFaq(data || null);
        }
      } catch (error) {
        console.error("FAQ fetch failed", error);

        if (isMounted) {
          setFaq(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadFaq();

    return () => {
      isMounted = false;
    };
  }, [program?.organization_id, program?.id]);

  const faqDocument = faq?.content ? buildFaqDocument(faq.content) : null;
  const normalizedQuery = query.trim().toLowerCase();
  const filteredQuestions = PARTICIPANT_FAQS.filter((item) => {
    if (!normalizedQuery) return true;

    const answerText = nodeText(item.answer);

    return (
      item.question.toLowerCase().includes(normalizedQuery) ||
      answerText.toLowerCase().includes(normalizedQuery)
    );
  });

  const contentSection = (
    <FaqContentSection
      faqDocument={faqDocument}
      filteredQuestions={filteredQuestions}
      loading={loading}
      activeQuestion={activeQuestion}
      setActiveQuestion={setActiveQuestion}
      query={query}
      setQuery={setQuery}
      t={t}
    />
  );

  // Plain elements, not components declared here: a component defined inside render is
  // a new type every render, so React remounted the page and the search box lost
  // its focus and value on each keystroke
  const clearFaqs = (
    <div className="faq-shell">
      <Container className="faq-shell__body">
        <ClearPageLayout>
          {contentSection}
        </ClearPageLayout>
      </Container>
    </div>
  );

  const classicFaqs = (
    <>
      <div className="mainboard">
        <img src={CLASSIC_IMG_BACK} alt="faqs" />
        <div className="title text-dark">{t("faqs")}</div>
      </div>

      <Container>
        <ParticipantTabNavs />
      </Container>

      <Container className="faq-shell__classic-body">
        <Row>
          <Col md={9}>{contentSection}</Col>
          <Col md={3}>
            <Sidebar />
          </Col>
        </Row>
      </Container>
    </>
  );

  return (
    (template?.name === "classic" && classicFaqs) ||
    (template?.name === "clear" && clearFaqs)
  );
};

const mapStateToProps = (state) => ({
  template: state.template,
  program: state.program,
});

export default connect(mapStateToProps)(Faqs);
